import { digger } from '@cbre/digger';
import { concatValuesBy, defaultTxt, formatNumber } from '@miq/utils';
import { ReportRecord } from '../../../models/report.model';
import { TemplateFieldsDisplayFn } from '../../../models/template.model';
import { sortAttributesByFieldId } from '../utils/argument-sorting';
import { Dash, displayFnWrapper, mapAndFormatDynamicValue } from '../utils/dynamic-data-retrievers';
import { getFloorAndSuite, getFloorPlanImageSlide } from './common';
import { STACKINGPLAN_FIELDS } from './stacking-plan-slide-common';

export const displayFnMap: TemplateFieldsDisplayFn = {
  /*
   * here 49('Property Name) is attribute id which is defined in DB.
   * Data coming from template api that belongs to the user and report.
   *  "49": {
   *    "id": 49,
   *    "label": "Property Name",
   *    "displayFnArguments": [
   *      {
   *        "id": 850,
   *        "entity": "property"
   *      }
   *    ]
   *  },
   * The above one example for attribute info.
   */
  ...STACKINGPLAN_FIELDS(),
  27: {
    displayFn: (record: ReportRecord, options, ...args) => {
      const finalArgs = sortAttributesByFieldId(options.locale, args, 27);
      const values = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
      return concatValuesBy(', ', ...values);
    },
    routeFn: ({ data: entityRecord }: ReportRecord) => {
      return ['/records', 'property', digger(entityRecord, 'property.id'), 'details'];
    },
  },
  49: {
    displayFn: (record: ReportRecord, options, arg1, arg2) => {
      const values = concatValuesBy(
        defaultTxt,
        mapAndFormatDynamicValue(record, arg1),
        mapAndFormatDynamicValue(record, arg2)
      );
      return values;
    },
  },
  78: { displayFn: displayFnWrapper },
  50: { displayFn: displayFnWrapper },
  79: { displayFn: displayFnWrapper },
  80: {
    displayFn: (record: ReportRecord, options, arg1, arg2) => {
      return concatValuesBy('&', mapAndFormatDynamicValue(record, arg1), mapAndFormatDynamicValue(record, arg2));
    },
  },
  81: {
    displayFn: (record: ReportRecord, options, arg) => {
      return Array.isArray(record.data) ? Dash : mapAndFormatDynamicValue(record, arg);
    },
  },
  82: {
    displayFn: (record: ReportRecord, options, arg) => {
      return Array.isArray(record.data) ? Dash : mapAndFormatDynamicValue(record, arg);
    },
  },
  96: { displayFn: displayFnWrapper },
  68: {
    displayFn: (record: ReportRecord, options, arg) => {
      return Array.isArray(record.data) ? Dash : mapAndFormatDynamicValue(record, arg);
    },
  },
  83: {
    displayFn: (record: ReportRecord, options, arg) => {
      if (Array.isArray(record.data)) {
        const { entity, field, id } = arg;
        let total = 0;
        let totalComposition = [];
        record.data.forEach(avail => {
          const askingRentVal = mapAndFormatDynamicValue(
            { ...record, data: avail },
            { dataType: 'id', entity, field, id }
          );
          total = total + Number(askingRentVal[0]);
          totalComposition = [...totalComposition, formatNumber(askingRentVal[0], undefined, arg.dataFormat)];
        });
        const result =
          formatNumber(total, undefined, arg.dataFormat) + ` SF(${concatValuesBy('+', ...totalComposition)})`;
        return result;
      }
      return mapAndFormatDynamicValue(record, arg);
    },
  },
  84: {
    displayFn: (record: ReportRecord, options, arg1, arg2, arg3) => {
      if (Array.isArray(record.data)) {
        const result = record.data.map(element => {
          const floor = mapAndFormatDynamicValue({ ...record, data: element }, arg2);
          const isWholeFloor = mapAndFormatDynamicValue({ ...record, data: element }, arg3);
          return (floor as string).trim() + '/' + (isWholeFloor === 'Yes' ? 'Whole Floor' : 'Part');
        });
        return concatValuesBy(', ', ...new Set(result));
      }
      return concatValuesBy(defaultTxt, mapAndFormatDynamicValue(record, arg1), mapAndFormatDynamicValue(record, arg2));
    },
  },
  85: { displayFn: displayFnWrapper },
  392: {
    displayFn: (record: ReportRecord, options, arg) => {
      return Array.isArray(record.data) ? Dash : mapAndFormatDynamicValue(record, arg);
    },
  },
  88: { displayFn: displayFnWrapper },
  89: { displayFn: displayFnWrapper },
  90: { displayFn: displayFnWrapper },
  91: {
    displayFn: (record: ReportRecord, options, arg) => {
      return Array.isArray(record.data) ? Dash : mapAndFormatDynamicValue(record, arg);
    },
  },
  92: { displayFn: displayFnWrapper },
  93: { displayFn: displayFnWrapper },
  94: { displayFn: displayFnWrapper },
  95: {
    displayFn: (record: ReportRecord, options, arg) => {
      return Array.isArray(record.data) ? Dash : mapAndFormatDynamicValue(record, arg);
    },
  },
  850: {
    displayFn: (record: ReportRecord) => {
      return digger(record.associatedProperty, 'property_name') || '-';
    },
  },
  ...getFloorAndSuite(),
  ...getFloorPlanImageSlide(),
};
